import { useState, useEffect, useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { buildShelfFormSchema } from "./shelfFormSchema";
import { Button, buttonRecipe } from "../../core/basic/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import { useCategoriesQuery } from "../../../hooks/useCategories";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { errorToast, successToast } from "../../../utils/toasts";
import { useAppSelector } from "../../../redux/hooks";
import { Select } from "../../fields/base/Select";
import { TextField } from "../../fields/base/TextField";
import { Datepicker } from "../../fields/base/Datepicker";
import { css } from "../../../../styled-system/css";
import { vstack } from "../../../../styled-system/patterns";
import { formField } from "../../../../styled-system/recipes";
import { Modal } from "../../core/basic/Modal";
import { KeywordListInput } from "../../fields/base/StringListInput.presets";
import { useSimpleCan } from "../../../config/casl/casl";
import { useMarketplaceLabel } from "../../../utils/marketplaceConstants";

const ShelfForm = (props) => {
  const { t } = useTranslation(["analytics"]);
  const getMarketplaceLabel = useMarketplaceLabel();
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const userCanUpdateDigitalShelf = useSimpleCan("update", "DigitalShelf");
  const userCanDeleteDigitalShelf = useSimpleCan("delete", "DigitalShelf");
  const userCanCreateDigitalShelf = useSimpleCan("create", "DigitalShelf");

  const starterKeywords = props.data?.searches?.map((search) => search.display || search.value) ?? [];
  if (props.autoKeyword) {
    starterKeywords.push(decodeURI(props.autoKeyword));
  }

  const shelfFormSchema = useMemo(() => buildShelfFormSchema(t), [t]);

  const { handleSubmit, control, getValues, reset } = useForm({
    resolver: zodResolver(shelfFormSchema),
    defaultValues: { ...props.data, category: props.data?.category ?? props.categoryFilter, keywords: starterKeywords },
    mode: "onChange",
  });

  const [shelfId, setShelfId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { data: categoryData } = useCategoriesQuery(marketPlace, "amazon");
  const categoryOptions = useMemo(
    () =>
      categoryData?.map((c) => ({
        label: c.leaf_category_name,
        value: c.id,
      })) ?? [],
    [categoryData],
  );

  useEffect(() => {
    if (!props.create) {
      setShelfId(props.data?.id);
    } else {
      reset({ name: "", description: "", brand_name: "" });
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data, props.brandData]);

  // Shelf CRUD requests
  const queryClient = useQueryClient();

  const createShelf = useMutation({
    mutationFn: async (newShelf) => {
      return await api.post("/api/shelves/", newShelf);
    },

    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ["shelves"],
      });
      if (res.status === 201) {
        if (props.modalForm) {
          props.closeModal();
        }
        props.updateShelfData("create", res.data.id);
        if (props.fullLayout) {
          props.setCategoryFilter(getValues("category"));
        }
        successToast({ message: t(($) => $.analytics.toasts["Shelf created successfully!"]) });
      }
    },
    onError: (error) => {
      errorToast({ message: t(($) => $.analytics.toasts["Failed to create shelf. Please try again."]), error });
    },
  });

  const updateShelf = useMutation({
    mutationFn: async (updatedShelf) => {
      return await api.put(`/api/shelves/${shelfId}/`, updatedShelf);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shelves"],
      });
      props.updateShelfData("update");
      successToast({ message: t(($) => $.analytics.toasts["Shelf updated successfully!"]) });
    },
    onError: (error) => {
      errorToast({ message: t(($) => $.analytics.toasts["Failed to update shelf. Please try again."]), error });
    },
  });

  const deleteShelf = useMutation({
    mutationFn: async () => {
      return await api.remove(`/api/shelves/${shelfId}/`);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shelves"],
      });
      props.updateShelfData("delete");
    },
  });

  return (
    <>
      {props.numOfShelfCredits <= props.numOfShelves ?
        <VStack paddingX="medium" paddingY="large" gap="large" textAlign={"center"}>
          <p
            className={css({
              fontSize: "xlarge",
              margin: 0,
            })}
          >
            {t(
              ($) =>
                $.analytics.shelf[
                  "Your subscription plan does not have any more available search analytics shelf credits."
                ],
            )}
          </p>
          <p
            className={css({
              fontSize: "xlarge",
              margin: 0,
            })}
          >
            {t(
              ($) =>
                $.analytics.shelf["To add more, please upgrade your subscription by clicking on the button below."],
            )}
          </p>
          <Link
            className={buttonRecipe()}
            to="/user/billing"
            state={{ showModal: "yes", numOfShelves: props.numOfShelves }}
          >
            {t(($) => $.analytics.shelf["Upgrade Subscription"])}
          </Link>
        </VStack>
      : <form className={vstack({ gap: "medium", width: "100%", alignItems: "stretch", paddingX: "large" })}>
          <HStack justifyContent="space-between" alignItems="center">
            <TextField
              label={t(($) => $.analytics.shelf.Marketplace)}
              value={getMarketplaceLabel(marketPlace)}
              isDisabled
            />
            <Datepicker
              label={t(($) => $.analytics.shelf["Creation Date"])}
              value={props.data?.created_on ?? new Date().toISOString()}
              disabled
              inputClassName={css({ width: "100%" })}
            />
          </HStack>
          <Controller
            name={"name"}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                errorMessage={fieldState.error?.message}
                label={t(($) => $.analytics.shelf["Shelf Name"])}
                placeholder={t(($) => $.analytics.shelf["Shelf Name"])}
                isDisabled={props.create ? !userCanCreateDigitalShelf : !userCanUpdateDigitalShelf}
              />
            )}
          />
          <Controller
            name={"description"}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                errorMessage={fieldState.error?.message}
                label={t(($) => $.analytics.shelf["Shelf Description"])}
                isDisabled={props.create ? !userCanCreateDigitalShelf : !userCanUpdateDigitalShelf}
              />
            )}
          />

          <HStack>
            <Controller
              name={"category"}
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  label={t(($) => $.analytics.shelf.Category)}
                  items={categoryOptions}
                  selectedKey={field.value}
                  errorMessage={fieldState.error?.message}
                  onSelectionChange={(value) => {
                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                  isDisabled={props.create ? !userCanCreateDigitalShelf : !userCanUpdateDigitalShelf}
                />
              )}
            />
            <Controller
              name={"brand_name"}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  errorMessage={fieldState.error?.message}
                  label={t(($) => $.analytics.shelf["Home Brand"])}
                  isDisabled={props.create ? !userCanCreateDigitalShelf : !userCanUpdateDigitalShelf}
                />
              )}
            />
          </HStack>

          <Controller
            name="keywords"
            control={control}
            render={({ field, fieldState }) => {
              const classNames = formField();
              return (
                <div className={classNames.fieldWrapper}>
                  <label className={classNames.label}>{t(($) => $.analytics.shelf.Keywords)}</label>
                  <KeywordListInput
                    bulkEditTitle={t(($) => $.analytics.shelf.Keywords)}
                    value={field.value ?? []}
                    onChange={(tags) => {
                      field.onChange(tags);
                    }}
                    isDisabled={props.create ? !userCanCreateDigitalShelf : !userCanUpdateDigitalShelf}
                    bulkEdit={props.create ? userCanCreateDigitalShelf : userCanUpdateDigitalShelf}
                  />
                  <div className={classNames.error}>{fieldState.error?.message}</div>
                </div>
              );
            }}
          />
          <HStack justifyContent="space-evenly" gap="medium">
            {props.create && (
              <Button
                variant="secondary"
                onPress={() => {
                  if (props.modalForm) {
                    props.closeModal();
                  }
                }}
              >
                {t(($) => $.analytics.shelf.Cancel)}
              </Button>
            )}

            {props.create ?
              <Button
                isPending={createShelf.isPending}
                isDisabled={!userCanCreateDigitalShelf}
                onPress={handleSubmit(
                  (values) => {
                    const shelfData = {
                      name: values.name,
                      description: values.description,
                      brand_name: values.brand_name,
                      marketplace: marketPlace,
                      channel: "AMZ",
                      category: values.category,
                      searches: values.keywords.map((keyword) => ({
                        search_type: "SEARCH",
                        value: keyword,
                        display: keyword,
                      })),
                    };

                    createShelf.mutate(shelfData);
                  },
                  (errors) => {
                    errorToast({
                      message: t(($) => $.analytics.toasts["Form validation failed. Please check the fields."]),
                      error: errors,
                    });
                  },
                )}
              >
                {t(($) => $.analytics.shelf.Create)}
              </Button>
            : <Button
                variant={"secondary"}
                isPending={updateShelf.isPending}
                isDisabled={!userCanUpdateDigitalShelf}
                onPress={handleSubmit(
                  (values) => {
                    const shelfData = {
                      name: values.name,
                      description: values.description,
                      brand_name: values.brand_name,
                      marketplace: marketPlace,
                      channel: "AMZ",
                      category: values.category,
                      searches: values.keywords.map((keyword) => ({
                        search_type: "SEARCH",
                        value: keyword,
                        display: keyword,
                      })),
                    };

                    updateShelf.mutate(shelfData);
                  },
                  (errors) => {
                    errorToast({
                      message: t(($) => $.analytics.toasts["Form validation failed. Please check the fields."]),
                      error: errors,
                    });
                  },
                )}
              >
                {t(($) => $.analytics.shelf["Update Shelf"])}
              </Button>
            }

            {!props.create && (
              <Button
                onPress={async () => {
                  setShowConfirmModal(true);
                }}
                isDisabled={!userCanDeleteDigitalShelf}
              >
                {t(($) => $.analytics.shelf["Delete Shelf"])}
              </Button>
            )}
          </HStack>
        </form>
      }

      <Modal
        isOpen={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        title={t(($) => $.analytics.shelf["Are you sure you want to permanently delete this shelf?"])}
      >
        <HStack justifyContent={"space-evenly"} gap="medium">
          <Button
            variant={"secondary"}
            onPress={() => {
              setShowConfirmModal(false);
            }}
          >
            {t(($) => $.analytics.shelf.Cancel)}
          </Button>
          <Button
            onPress={() => {
              deleteShelf.mutate();
              setShowConfirmModal(false);
            }}
          >
            {t(($) => $.analytics.shelf.Delete)}
          </Button>
        </HStack>
      </Modal>
    </>
  );
};

export default ShelfForm;
